import ArchiveIcon from "@redotech/redo-icons/archive.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import CornerUpLeftDoubleIcon from "@redotech/redo-icons/corner-up-left-double.svg";
import EyeOffIcon from "@redotech/redo-icons/eye-off_filled.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import FlagIcon from "@redotech/redo-icons/flag-01.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipForwardIcon from "@redotech/redo-icons/flip-forward.svg";
import GitMergeIcon from "@redotech/redo-icons/git-merge_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import RefreshCwIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import SendIcon from "@redotech/redo-icons/send-01.svg";
import SlashOctagonIcon from "@redotech/redo-icons/slash-octagon_filled.svg";
import StickerSquareIcon from "@redotech/redo-icons/sticker-square.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import type { ComponentType } from "react";
import type { KeyboardShortcut } from "../../keyboard-shortcuts/keyboard-shortcut";
import {
  messageInputShortcuts,
  priorityShortcuts,
  snoozeShortcuts,
  supportTicketActionShortcuts,
  tableNavigationShortcuts,
} from "../../keyboard-shortcuts/shopper-ai-shortcuts";
import {
  supportTicketActionIds,
  type SupportTicketActionId,
} from "./action-ids";

/**
 * Static action definition - UI metadata only.
 * Does not include isEnabled/isVisible which depend on domain context.
 */
export interface StaticActionDefinition {
  id: SupportTicketActionId;
  title: string;
  description: string;
  shortcut?: KeyboardShortcut;
  Icon?: ComponentType<{ className?: string }>;
  category?: string;
}

/**
 * All support ticket action definitions with static metadata.
 * Domain-specific logic (isEnabled, isVisible) is added in support-v2.
 */
export const supportTicketActionDefinitions: Record<
  SupportTicketActionId,
  StaticActionDefinition
> = {
  // Table navigation
  [supportTicketActionIds.navigateUp]: {
    id: supportTicketActionIds.navigateUp,
    title: "Navigate Up",
    description: "Move selection up in the list",
    shortcut: tableNavigationShortcuts.NAVIGATE_UP,
  },
  [supportTicketActionIds.navigateDown]: {
    id: supportTicketActionIds.navigateDown,
    title: "Navigate Down",
    description: "Move selection down in the list",
    shortcut: tableNavigationShortcuts.NAVIGATE_DOWN,
  },
  [supportTicketActionIds.openSupportTicket]: {
    id: supportTicketActionIds.openSupportTicket,
    title: "Open",
    description: "Open the selected support ticket",
    shortcut: tableNavigationShortcuts.OPEN_SUPPORT_TICKET,
  },
  [supportTicketActionIds.openActionMenu]: {
    id: supportTicketActionIds.openActionMenu,
    title: "Actions",
    description: "Open the action menu for the selected support ticket",
    shortcut: tableNavigationShortcuts.OPEN_ACTION_MENU,
  },

  // Direct support ticket actions
  [supportTicketActionIds.close]: {
    id: supportTicketActionIds.close,
    title: "Close",
    description: "Mark the support ticket as closed",
    shortcut: supportTicketActionShortcuts.CLOSE,
    Icon: CheckCircleIcon,
  },
  [supportTicketActionIds.reopen]: {
    id: supportTicketActionIds.reopen,
    title: "Reopen",
    description: "Reopen the support ticket",
    shortcut: supportTicketActionShortcuts.REOPEN,
    Icon: RefreshCwIcon,
  },
  [supportTicketActionIds.markInProgress]: {
    id: supportTicketActionIds.markInProgress,
    title: "Mark as in progress",
    description: "Mark the support ticket as in progress",
    shortcut: supportTicketActionShortcuts.MARK_IN_PROGRESS,
    Icon: PlayCircleIcon,
  },
  [supportTicketActionIds.snooze]: {
    id: supportTicketActionIds.snooze,
    title: "Snooze",
    description: "Snooze the support ticket until later",
    shortcut: supportTicketActionShortcuts.SNOOZE,
    Icon: ClockIcon,
  },
  [supportTicketActionIds.archive]: {
    id: supportTicketActionIds.archive,
    title: "Archive",
    description: "Archive the support ticket",
    shortcut: supportTicketActionShortcuts.ARCHIVE,
    Icon: ArchiveIcon,
  },
  [supportTicketActionIds.delete]: {
    id: supportTicketActionIds.delete,
    title: "Delete",
    description: "Delete the support ticket",
    shortcut: supportTicketActionShortcuts.DELETE,
    Icon: TrashIcon,
  },
  [supportTicketActionIds.markRead]: {
    id: supportTicketActionIds.markRead,
    title: "Mark as read",
    description: "Mark the support ticket as read",
    shortcut: supportTicketActionShortcuts.MARK_READ,
    Icon: EyeIcon,
  },
  [supportTicketActionIds.markUnread]: {
    id: supportTicketActionIds.markUnread,
    title: "Mark as unread",
    description: "Mark the support ticket as unread",
    shortcut: supportTicketActionShortcuts.MARK_UNREAD,
    Icon: EyeOffIcon,
  },
  [supportTicketActionIds.markSpam]: {
    id: supportTicketActionIds.markSpam,
    title: "Mark as spam",
    description:
      "Close the ticket, tag it as spam, and exclude the sender's email",
    Icon: SlashOctagonIcon,
  },
  [supportTicketActionIds.unmarkSpam]: {
    id: supportTicketActionIds.unmarkSpam,
    title: "Unmark as spam",
    description:
      "Reopen the ticket, remove the spam tag, and remove the sender from the exclusion list",
    Icon: SlashOctagonIcon,
  },

  // Submenu trigger actions
  [supportTicketActionIds.setPriority]: {
    id: supportTicketActionIds.setPriority,
    title: "Set priority",
    description: "Set the priority of the support ticket",
    shortcut: supportTicketActionShortcuts.SET_PRIORITY,
    Icon: FlagIcon,
  },
  [supportTicketActionIds.assign]: {
    id: supportTicketActionIds.assign,
    title: "Assign",
    description: "Assign the support ticket to a team member",
    shortcut: supportTicketActionShortcuts.ASSIGN,
    Icon: UserIcon,
  },
  [supportTicketActionIds.assignToMe]: {
    id: supportTicketActionIds.assignToMe,
    title: "Assign to me",
    description: "Assign the support ticket to the current user",
    Icon: UserIcon,
  },
  [supportTicketActionIds.addTag]: {
    id: supportTicketActionIds.addTag,
    title: "Manage tags",
    description: "Manage tags for the support ticket",
    shortcut: supportTicketActionShortcuts.ADD_TAG,
    Icon: TagIcon,
  },

  // Priority options
  [supportTicketActionIds.priorityUrgent]: {
    id: supportTicketActionIds.priorityUrgent,
    title: "Urgent",
    description: "Set priority to urgent",
    shortcut: priorityShortcuts.PRIORITY_URGENT,
  },
  [supportTicketActionIds.priorityClear]: {
    id: supportTicketActionIds.priorityClear,
    title: "Clear priority",
    description: "Remove priority from the support ticket",
    shortcut: priorityShortcuts.PRIORITY_CLEAR,
  },

  // Snooze options
  [supportTicketActionIds.snooze1h]: {
    id: supportTicketActionIds.snooze1h,
    title: "1 hour",
    description: "Snooze for 1 hour",
    shortcut: snoozeShortcuts.SNOOZE_1H,
  },
  [supportTicketActionIds.snooze4h]: {
    id: supportTicketActionIds.snooze4h,
    title: "4 hours",
    description: "Snooze for 4 hours",
    shortcut: snoozeShortcuts.SNOOZE_4H,
  },
  [supportTicketActionIds.snooze24h]: {
    id: supportTicketActionIds.snooze24h,
    title: "24 hours",
    description: "Snooze for 24 hours",
    shortcut: snoozeShortcuts.SNOOZE_24H,
  },
  [supportTicketActionIds.snoozeCustom]: {
    id: supportTicketActionIds.snoozeCustom,
    title: "Custom...",
    description: "Set a custom snooze time",
    shortcut: snoozeShortcuts.SNOOZE_CUSTOM,
  },
  [supportTicketActionIds.snoozeClear]: {
    id: supportTicketActionIds.snoozeClear,
    title: "Clear Snooze",
    description: "Clear the snooze setting",
    shortcut: snoozeShortcuts.CLEAR_SNOOZE,
  },

  // Merge action
  [supportTicketActionIds.mergeTicket]: {
    id: supportTicketActionIds.mergeTicket,
    title: "Merge",
    description: "Merge this ticket with another",
    Icon: GitMergeIcon,
    shortcut: supportTicketActionShortcuts.MERGE,
  },

  // Message input actions
  [supportTicketActionIds.sendMessage]: {
    id: supportTicketActionIds.sendMessage,
    title: "Send",
    description: "Send the message",
    shortcut: messageInputShortcuts.SEND_MESSAGE,
    Icon: SendIcon,
  },
  [supportTicketActionIds.deleteDraft]: {
    id: supportTicketActionIds.deleteDraft,
    title: "Delete Draft",
    description: "Delete the current draft",
    Icon: TrashIcon,
  },

  // Reply mode actions
  [supportTicketActionIds.reply]: {
    id: supportTicketActionIds.reply,
    title: "Reply",
    description: "Reply to the sender",
    Icon: FlipBackwardIcon,
  },
  [supportTicketActionIds.replyAll]: {
    id: supportTicketActionIds.replyAll,
    title: "Reply all",
    description: "Reply to all recipients",
    Icon: CornerUpLeftDoubleIcon,
  },
  [supportTicketActionIds.forward]: {
    id: supportTicketActionIds.forward,
    title: "Forward",
    description: "Forward the message",
    Icon: FlipForwardIcon,
  },
  [supportTicketActionIds.internalNote]: {
    id: supportTicketActionIds.internalNote,
    title: "Internal note",
    description: "Add an internal note visible only to your team",
    Icon: StickerSquareIcon,
  },

  // Meta comment actions (Facebook/Instagram)
  [supportTicketActionIds.hideComment]: {
    id: supportTicketActionIds.hideComment,
    title: "Hide comment",
    description: "Hide this comment from public view",
    Icon: EyeOffIcon,
  },
  [supportTicketActionIds.unhideComment]: {
    id: supportTicketActionIds.unhideComment,
    title: "Unhide comment",
    description: "Make this comment visible again",
    Icon: EyeIcon,
  },
  [supportTicketActionIds.deleteComment]: {
    id: supportTicketActionIds.deleteComment,
    title: "Delete comment",
    description: "Permanently delete this comment",
    Icon: TrashIcon,
  },
  [supportTicketActionIds.likeComment]: {
    id: supportTicketActionIds.likeComment,
    title: "Like comment",
    description: "Like this comment",
    Icon: FlagIcon,
  },
  [supportTicketActionIds.unlikeComment]: {
    id: supportTicketActionIds.unlikeComment,
    title: "Unlike comment",
    description: "Remove like from this comment",
    Icon: FlagIcon,
  },
  [supportTicketActionIds.updateComment]: {
    id: supportTicketActionIds.updateComment,
    title: "Edit comment",
    description: "Edit the comment text",
    Icon: TrashIcon,
  },
};

/**
 * Get a static action definition by ID.
 */
export function getStaticActionDefinition(
  id: SupportTicketActionId,
): StaticActionDefinition {
  return supportTicketActionDefinitions[id];
}
